import { lazyOpenBookingCompose } from 'bookwithme-routes';
import {
    DEFAULT_PALETTEID,
    getFullBackgroundImageUrl,
    getSimpleDurationString,
} from 'bookwithme-shared';
import { logUsage } from 'owa-analytics';
import { isFeatureEnabled } from 'owa-feature-flags';
import CopyRegular from 'owa-fluent-icons-svg/lib/icons/CopyRegular';
import DeleteRegular from 'owa-fluent-icons-svg/lib/icons/DeleteRegular';
import LinkDismissRegular from 'owa-fluent-icons-svg/lib/icons/LinkDismissRegular';
import LockClosedRegular from 'owa-fluent-icons-svg/lib/icons/LockClosedRegular';
import LockOpenRegular from 'owa-fluent-icons-svg/lib/icons/LockOpenRegular';
import SaveCopyRegular from 'owa-fluent-icons-svg/lib/icons/SaveCopyRegular';
import ShareRegular from 'owa-fluent-icons-svg/lib/icons/ShareRegular';
import StarEmphasisRegular from 'owa-fluent-icons-svg/lib/icons/StarEmphasisRegular';
import loc, { format } from 'owa-localize';
import { lazyShowNotification } from 'owa-notification-bar';
import { getUserDisplayName } from 'owa-session-store';

import setMeetingTypeDuration from '../../actions/setMeetingTypeDuration';
import setMeetingTypeId from '../../actions/setMeetingTypeId';
import getMailboxPuid from '../../selectors/getMailboxPuid';
import { setMeetingTypeName } from '../../actions/setMeetingTypeName';
import {
    setShareLinkIsAttendeeAuthRequired,
    setShareLinkIsSingleUse,
    setShareModalInfo,
    setShowShareModal,
    shareModalDismiss,
} from '../../actions/shareModalActions';
import getEntryPoint from '../../selectors/getEntryPoint';
import getPublishedStatus from '../../selectors/getPublishedStatus';
import isShareDisabled from '../../selectors/isShareDisabled';
import BookingPrivacyType from '../../store/schema/BookingPrivacyType';
import store from '../../store/Store';
import { BookingCard } from '../../utils/copyBookingCard';
import copyHelper from '../../utils/copyHelper';
import deleteMeetingType from '../../utils/deleteMeetingType';
import enableDisableMeetingType from '../../utils/enableDisableMeetingType';
import getBookingUrl from '../../utils/getBookingUrl';
import { getExpiringDateDetail } from '../../utils/getCardDetailFunctions';
import { getUserMailboxGuid } from '../../utils/getMailboxGuid';
import { mapServiceToMeetingTypeEditState } from '../../utils/mapServiceToMeetingTypeEditState';
import { parseMeetingTypeDescription } from '../../utils/parseServiceDescription';
import updateMeetingTypePrivacy from '../../utils/updateMeetingTypePrivacy';
import {
    bookWithMeCopyLinkFailure,
    bookWithMeCopyLinkSuccess,
    bookWithMeCopyMeetingTypeDisplayRenameText,
} from '../compose/ComposeForm.locstring.json';
import {
    bookWithMeDeleteButtonLabel,
    bookWithMeDeleteButtonTooltip,
} from '../compose/SidebarButtons.locstring.json';
import { contextualMenuItem, contextualMenuItemUnified } from '../shared/EventListItem.scss';
import { setNavigationStart } from 'bookingsv2-utils/lib/analytics/getTimeFromFetchStart';

import {
    bookWithMeBookATimeWithMeAndTitleText,
    bookwithmeDisabledMeetingTypesShareButtonToolTipMessage,
    bookWithMeCopyBookingLinkTooltip,
    unifiedBookingsCopyBookingLinkText,
    bookWithMeCopyBookingLinkText,
    bookWithMeCopySingleUseLinkTooltip,
    bookWithMeCopySingleUseBookingLinkText,
    bookWithMeShareLinkModalTitle,
    bookwithme_shareBookingButtonLabel,
    bookWithMeMakePrivateText,
    bookWithMeMakePrivateTooltip,
    bookWithMeMakePublicText,
    bookWithMeMakePublicTooltip,
    bookwithme_duplicateMeetingTypeButtonLabel,
    bookwithme_duplicateMeetingTypeButtonTooltip,
    bookwithme_stopSharingMeetingTypeButtonLabel,
    bookwithme_stopSharingMeetingTypeButtonTooltip,
} from '../shared/ShareModal.locstring.json';
import isDefaultMeetingType from '../../selectors/isDefaultMeetingType';

import type { IContextualMenuItem, IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';

import type BookingMeetingType from 'owa-service/lib/contract/BookingService';
import { isFirstSessionUser } from '../../selectors/getUserOnboardingState';
import getLinkWithEntryPoint from '../../utils/getLinkWithEntryPoint';
import type EntryPoint from '../../store/schema/EntryPoint';
import getPageOrigin from '../../selectors/getPageOrigin';
import { getBookingServiceAction } from '../../actions/getBookingServiceAction';
import changeCurrentRoute from 'bookings-router-store-v2/lib/mutators/changeCurrentRoute';
import { type BookingsRoute } from 'bookingsv2-constants/lib/bookingsRouteConstants';
import getPrivateAndPublicMeetingTypes from '../../selectors/getPrivateAndPublicMeetingTypes';
import { getBookingsRouterStore } from 'bookings-router-store-v2/lib/store';
import { isSmallBusiness, isVerySmallBusiness } from 'owa-bpos-utils';

export function shareMeetingType(meetingType: BookingMeetingType) {
    const meetingTypeId = meetingType.ServiceId ?? '';
    const meetingTypeDescription = parseMeetingTypeDescription(meetingType.Description ?? '');
    const description = meetingTypeDescription.description;
    const themeImageUrl = getFullBackgroundImageUrl(
        store.pbpPublishingSetting.publishingSettings.Theme?.PaletteId ?? DEFAULT_PALETTEID
    );
    const isPrivateMeetingType =
        meetingType.ExcludeFromSelfService ||
        meetingTypeDescription.privacyType == BookingPrivacyType.PrivateMeetingType;

    const meetingTypeStates = getPrivateAndPublicMeetingTypes();
    const publicMeetingTypes = meetingTypeStates.publicMeetingTypes;
    const privateMeetingTypes = meetingTypeStates.privateMeetingTypes;
    const userSmbOrVsb = isSmallBusiness() ? 'Smb' : isVerySmallBusiness() ? 'Vsb' : 'Enterprise';

    const shareLinkHandler = () => {
        setShareLinkIsSingleUse(false);
        setShareLinkIsAttendeeAuthRequired(false);
        setShareModalInfo(
            meetingTypeId,
            meetingType.DisplayName ?? '',
            themeImageUrl,
            getSimpleDurationString(meetingType.DefaultDurationMinutes ?? 0) ?? '',
            description ?? '',
            [] /*allowedAttendees*/,
            isPrivateMeetingType /*isPrivate*/
        );

        getBookingUrl()
            .then(() => {
                if (store.sharedViewState.shareLink) {
                    setShowShareModal(true);
                } else {
                    throw new Error('No valid share link');
                }
            })
            .catch(() => {
                lazyShowNotification.importAndExecute(
                    'copyBookingLinkFailed',
                    'BookWithMeNotificationBarHost',
                    window,
                    loc(bookWithMeCopyLinkFailure),
                    { primaryActionHotKeyCommand: '' }
                );
            });
    };
    shareLinkHandler();
    isDefaultMeetingType(meetingTypeId) &&
        logUsage('defaultServiceActionInitiated', {
            actionType: 'shareAction',
            src: 'contextMenu',
            isFirstSessionUser: isFirstSessionUser(),
            entryPoint: getEntryPoint(),
            bookingMailboxPuid: getMailboxPuid(),
            meetingTypesCount: publicMeetingTypes.length + privateMeetingTypes.length,
            isPublished: store.pbpPublishingSetting.publishingSettings.IsPublished,
            isUnified: true,
            isSMBUser: userSmbOrVsb,
        });

    logUsage('onEmailLinkButtonClick', {
        actionType: 'shareAction',
        src: 'contextMenu',
        isFirstSessionUser: isFirstSessionUser(),
        entryPoint: getEntryPoint(),
        bookingMailboxPuid: getMailboxPuid(),
        meetingTypesCount: publicMeetingTypes.length + privateMeetingTypes.length,
        isPublished: store.pbpPublishingSetting.publishingSettings.IsPublished,
        isUnified: true,
        isSMBUser: userSmbOrVsb,
    });

    setMeetingTypeDuration(meetingType.DefaultDurationMinutes ?? 0);
}

export default function getEventListItemMoreOptionsMenu(
    meetingType: BookingMeetingType,
    isFRETriggered?: boolean
): IContextualMenuProps {
    const meetingTypeId = meetingType.ServiceId ?? '';
    const meetingTypeDescription = parseMeetingTypeDescription(meetingType.Description ?? '');
    const description = meetingTypeDescription.description;
    const themeImageUrl = getFullBackgroundImageUrl(
        store.pbpPublishingSetting.publishingSettings.Theme?.PaletteId ?? DEFAULT_PALETTEID
    );
    const isPrivateMeetingType =
        meetingType.ExcludeFromSelfService ||
        meetingTypeDescription.privacyType == BookingPrivacyType.PrivateMeetingType;

    const meetingTypeStates = getPrivateAndPublicMeetingTypes();
    const publicMeetingTypes = meetingTypeStates.publicMeetingTypes;
    const privateMeetingTypes = meetingTypeStates.privateMeetingTypes;
    const isUnifiedLandingPage = getBookingsRouterStore().currentRoute === 'homepage';
    const userSmbOrVsb = isSmallBusiness() ? 'Smb' : isVerySmallBusiness() ? 'Vsb' : 'Enterprise';

    const shareLinkHandler = () => {
        setShareLinkIsSingleUse(false);
        setShareLinkIsAttendeeAuthRequired(false);
        setShareModalInfo(
            meetingTypeId,
            meetingType.DisplayName ?? '',
            themeImageUrl,
            getSimpleDurationString(meetingType.DefaultDurationMinutes ?? 0) ?? '',
            description ?? '',
            [] /*allowedAttendees*/,
            isPrivateMeetingType /*isPrivate*/
        );

        getBookingUrl()
            .then(() => {
                if (store.sharedViewState.shareLink) {
                    setShowShareModal(true);
                } else {
                    throw new Error('No valid share link');
                }
            })
            .catch(() => {
                lazyShowNotification.importAndExecute(
                    'copyBookingLinkFailed',
                    'BookWithMeNotificationBarHost',
                    window,
                    loc(bookWithMeCopyLinkFailure),
                    { primaryActionHotKeyCommand: '' }
                );
            });
    };

    const onShareModalDismiss = () => {
        shareModalDismiss();
    };

    const copyLinkHandler = (shareLink: string) => {
        const displayName = getUserDisplayName() ?? '';
        const shareText = format(
            loc(bookWithMeBookATimeWithMeAndTitleText),
            displayName,
            store.sharedViewState.shareEventTitle
        );
        const expiringDateDetail = getExpiringDateDetail();
        // Keeping this in case We need to add more details
        // for now just the expiration date
        const meetingTypeDetails = expiringDateDetail ? '\u2022 ' + expiringDateDetail : '';
        const formattedCardString = BookingCard.replace('##BOOKING_LINK##', shareLink)
            .replace('##BOOKING_TITLE##', shareText)
            .replace('##BOOKING_DETAILS##', meetingTypeDetails);

        return formattedCardString;
    };

    const onCopyLinkClick = () => {
        isDefaultMeetingType(meetingTypeId) &&
            logUsage('defaultServiceActionInitiated', {
                actionType: 'copyAction',
                src: 'contextMenu',
                isFirstSessionUser: isFirstSessionUser(),
                bookingMailboxPuid: getMailboxPuid(),
                meetingTypesCount: publicMeetingTypes.length + privateMeetingTypes.length,
                isPublished: store.pbpPublishingSetting.publishingSettings.IsPublished,
                isUnified: true,
                isSMBUser: userSmbOrVsb,
            });
        setShareLinkIsSingleUse(false);
        setShareLinkIsAttendeeAuthRequired(false);
        setShareModalInfo(
            meetingTypeId,
            meetingType.DisplayName ?? '',
            themeImageUrl,
            getSimpleDurationString(meetingType.DefaultDurationMinutes ?? 0) ?? '',
            description ?? '',
            [] /*allowedAttendees*/,
            isPrivateMeetingType /*isPrivate*/
        );

        // Here we are using a promise since we don't have the right value until after a network request
        const bookingUrlPromise = getBookingUrl();

        copyHelper({
            'text/plain': bookingUrlPromise
                .then((response: string) => getLinkWithEntryPoint(response, 8))
                .catch(() => ''),
            'text/html': bookingUrlPromise
                .then((response: string) => copyLinkHandler(getLinkWithEntryPoint(response, 9)))
                .catch(() => ''),
        });

        bookingUrlPromise
            .then(() => {
                logUsage('BwmServiceLinkCopied', {
                    pageOrigin: getPageOrigin(),
                    entryPoint: getEntryPoint(),
                    meetingTypesCount: publicMeetingTypes.length + privateMeetingTypes.length,
                    isPublished: store.pbpPublishingSetting.publishingSettings.IsPublished,
                    isUnified: true,
                    isSMBUser: userSmbOrVsb,
                });

                logUsage('onCopyMeetingTypeLink', {
                    entryPoint: getEntryPoint(),
                    mailboxGuid: getUserMailboxGuid(),
                    meetingTypeId: store.sharedViewState.shareEventId,
                    privacyType: store.sharedViewState.isPrivate,
                    isSingleUse: store.sharedViewState.isSingleUse,
                    bookingMailboxPuid: getMailboxPuid(),
                    meetingTypesCount: publicMeetingTypes.length + privateMeetingTypes.length,
                    isPublished: store.pbpPublishingSetting.publishingSettings.IsPublished,
                    isUnified: true,
                    isSMBUser: userSmbOrVsb,
                });

                lazyShowNotification.importAndExecute(
                    'copyBookingLinkSuccessed',
                    'BookWithMeNotificationBarHost',
                    window,
                    loc(bookWithMeCopyLinkSuccess),
                    { primaryActionHotKeyCommand: '', minWidth: 300 }
                );
                // Fix: Unexpected share modal displays when opening meeting type compose.
                onShareModalDismiss(); // adding the dismiss to close completely the interaction with the modal, in other pages
            })
            .catch(() => {
                lazyShowNotification.importAndExecute(
                    'copyBookingLinkFailed',
                    'BookWithMeNotificationBarHost',
                    window,
                    loc(bookWithMeCopyLinkFailure),
                    { primaryActionHotKeyCommand: '' }
                );
                // Fix: Unexpected share modal displays when opening meeting type compose.
                onShareModalDismiss(); // adding the dismiss to close completely the interaction with the modal, in other pages
            });
    };

    const onCopySingleUseLinkClick = () => {
        setShareLinkIsSingleUse(true);
        setShareLinkIsAttendeeAuthRequired(false);
        setShareModalInfo(
            meetingTypeId,
            meetingType.DisplayName ?? '',
            themeImageUrl,
            getSimpleDurationString(meetingType.DefaultDurationMinutes ?? 0) ?? '',
            description ?? '',
            [] /*allowedAttendees*/,
            isPrivateMeetingType /*isPrivate*/
        );

        const bookingUrlPromise = getBookingUrl();

        copyHelper({
            'text/plain': bookingUrlPromise
                .then((response: string) => getLinkWithEntryPoint(response, 10))
                .catch(() => ''),
            'text/html': bookingUrlPromise
                .then((response: string) => copyLinkHandler(getLinkWithEntryPoint(response, 11)))
                .catch(() => ''),
        });

        bookingUrlPromise
            .then(() => {
                logUsage('BwmServiceSingleUseLinkCopied', {
                    pageOrigin: getPageOrigin(),
                    entryPoint: getEntryPoint(),
                    meetingTypesCount: publicMeetingTypes.length + privateMeetingTypes.length,
                    isPublished: store.pbpPublishingSetting.publishingSettings.IsPublished,
                    isUnified: true,
                    isSMBUser: userSmbOrVsb,
                });

                logUsage('onCopyMeetingTypeLink', {
                    entryPoint: getEntryPoint(),
                    meetingTypeId: store.sharedViewState.shareEventId,
                    privacyType: store.sharedViewState.isPrivate,
                    isSingleUse: store.sharedViewState.isSingleUse,
                    bookingMailboxPuid: getMailboxPuid(),
                    meetingTypesCount: publicMeetingTypes.length + privateMeetingTypes.length,
                    isPublished: store.pbpPublishingSetting.publishingSettings.IsPublished,
                    isUnified: true,
                    isSMBUser: userSmbOrVsb,
                });

                lazyShowNotification.importAndExecute(
                    'copyBookingLinkSuccessed',
                    'BookWithMeNotificationBarHost',
                    window,
                    loc(bookWithMeCopyLinkSuccess),
                    { primaryActionHotKeyCommand: '', minWidth: 300 }
                );
                // Fix: Unexpected share modal displays when opening meeting type compose.
                onShareModalDismiss(); // adding the dismiss to close completely the interaction with the modal, in other pages
            })
            .catch(() => {
                lazyShowNotification.importAndExecute(
                    'copyBookingLinkFailed',
                    'BookWithMeNotificationBarHost',
                    window,
                    loc(bookWithMeCopyLinkFailure),
                    { primaryActionHotKeyCommand: '' }
                );
                // Fix: Unexpected share modal displays when opening meeting type compose.
                onShareModalDismiss(); // adding the dismiss to close completely the interaction with the modal, in other pages
            });
    };

    const onEmailLinkButtonClick = () => {
        shareLinkHandler();
        isDefaultMeetingType(meetingTypeId) &&
            logUsage('defaultServiceActionInitiated', {
                actionType: 'shareAction',
                src: 'contextMenu',
                isFirstSessionUser: isFirstSessionUser(),
                entryPoint: getEntryPoint(),
                bookingMailboxPuid: getMailboxPuid(),
                meetingTypesCount: publicMeetingTypes.length + privateMeetingTypes.length,
                isPublished: store.pbpPublishingSetting.publishingSettings.IsPublished,
                isUnified: true,
            });

        logUsage('onEmailLinkButtonClick', {
            actionType: 'shareAction',
            src: 'contextMenu',
            isFirstSessionUser: isFirstSessionUser(),
            entryPoint: getEntryPoint(),
            bookingMailboxPuid: getMailboxPuid(),
            meetingTypesCount: publicMeetingTypes.length + privateMeetingTypes.length,
            isPublished: store.pbpPublishingSetting.publishingSettings.IsPublished,
            isUnified: true,
        });

        setMeetingTypeDuration(meetingType.DefaultDurationMinutes ?? 0);
    };

    const onStopSharingMeetingTypeButtonClick = () => {
        enableDisableMeetingType(meetingTypeId, false);
    };

    const onMakePublicMeetingTypeButtonClick = () => {
        updateMeetingTypePrivacy(meetingTypeId, true /*makePublic*/, false /*excludeFromService*/);
    };

    const onMakePrivateMeetingTypeButtonClick = () => {
        isDefaultMeetingType(meetingTypeId) &&
            logUsage('defaultServiceActionInitiated', {
                actionType: 'makePrivateAction',
                src: 'contextMenu',
                isFirstSessionUser: isFirstSessionUser(),
                bookingMailboxPuid: getMailboxPuid(),
                meetingTypesCount: publicMeetingTypes.length + privateMeetingTypes.length,
                isPublished: store.pbpPublishingSetting.publishingSettings.IsPublished,
                isUnified: true,
            });
        updateMeetingTypePrivacy(meetingTypeId, false /*makePublic*/, false /*excludeFromService*/);
    };

    const onDeleteMeetingTypeButtonClicked = () => {
        isDefaultMeetingType(meetingTypeId) &&
            logUsage('defaultServiceActionInitiated', {
                actionType: 'deleteAction',
                src: 'contextMenu',
                isFirstSessionUser: isFirstSessionUser(),
                bookingMailboxPuid: getMailboxPuid(),
                meetingTypesCount: publicMeetingTypes.length + privateMeetingTypes.length,
                isPublished: store.pbpPublishingSetting.publishingSettings.IsPublished,
                isUnified: true,
            });
        deleteMeetingType(meetingTypeId);
    };

    const onDuplicateMeetingTypeButtonClicked = () => {
        isDefaultMeetingType(meetingTypeId) &&
            logUsage('defaultServiceActionInitiated', {
                actionType: 'duplicateAction',
                src: 'contextMenu',
                isFirstSessionUser: isFirstSessionUser(),
                bookingMailboxPuid: getMailboxPuid(),
                meetingTypesCount: publicMeetingTypes.length + privateMeetingTypes.length,
                isPublished: store.pbpPublishingSetting.publishingSettings.IsPublished,
                isUnified: true,
            });
        mapServiceToMeetingTypeEditState(meetingType);
        // To handle the case while in model A (as meetingType will not have inboxReminders populated)
        const meetingId = meetingType?.ItemId;
        if (meetingId && !isFeatureEnabled('bookwithme-c1B2Migration')) {
            getBookingServiceAction(meetingId);
        }
        const copyName =
            meetingType.DisplayName + ' - ' + loc(bookWithMeCopyMeetingTypeDisplayRenameText);
        setMeetingTypeName(copyName);
        setMeetingTypeId('');

        logUsage('DuplicateMeetingType', {
            entryPoint: getEntryPoint(),
            meetingTypeId,
            bookingMailboxPuid: getMailboxPuid(),
            meetingTypesCount: publicMeetingTypes.length + privateMeetingTypes.length,
            isPublished: store.pbpPublishingSetting.publishingSettings.IsPublished,
            isUnified: true,
        });
        const currentRoute = getBookingsRouterStore().currentRoute;
        if (currentRoute === 'homepage') {
            setNavigationStart(performance.now());
            changeCurrentRoute('newmeetingtypecompose');
        } else {
            lazyOpenBookingCompose.importAndExecute();
        }
    };

    const dismissClick = () => {};

    const disableShareButton = isShareDisabled();
    const copyLinkMeetingTypeTooltip = disableShareButton
        ? loc(bookwithmeDisabledMeetingTypesShareButtonToolTipMessage)
        : loc(bookWithMeCopyBookingLinkTooltip);
    const copyLinkMenuItem: IContextualMenuItem = {
        key: 'CopyLink',
        text: isUnifiedLandingPage
            ? loc(unifiedBookingsCopyBookingLinkText)
            : loc(bookWithMeCopyBookingLinkText),
        title: copyLinkMeetingTypeTooltip,
        iconProps: {
            iconName: CopyRegular,
            styles: {
                root: isUnifiedLandingPage ? contextualMenuItemUnified : contextualMenuItem,
            },
        },
        disabled: disableShareButton,
        onClick: isFRETriggered ? dismissClick : onCopyLinkClick,
    };

    const copySingleUseMeetingTypeTooltip = disableShareButton
        ? loc(bookwithmeDisabledMeetingTypesShareButtonToolTipMessage)
        : loc(bookWithMeCopySingleUseLinkTooltip);
    const copySingleUseLinkMenuItem: IContextualMenuItem = {
        key: 'CopySingleUseLink',
        text: loc(bookWithMeCopySingleUseBookingLinkText),
        title: copySingleUseMeetingTypeTooltip,
        iconProps: {
            iconName: StarEmphasisRegular,
            styles: {
                root: isUnifiedLandingPage ? contextualMenuItemUnified : contextualMenuItem,
            },
        },
        disabled: disableShareButton,
        onClick: onCopySingleUseLinkClick,
    };

    const shareMeetingTypeTooltip = disableShareButton
        ? loc(bookwithmeDisabledMeetingTypesShareButtonToolTipMessage)
        : loc(bookWithMeShareLinkModalTitle);
    const shareViaEmailMenuItem: IContextualMenuItem = {
        key: 'ShareViaEmail',
        title: shareMeetingTypeTooltip,
        iconProps: {
            iconName: ShareRegular,
            styles: {
                root: isUnifiedLandingPage ? contextualMenuItemUnified : contextualMenuItem,
            },
        },
        disabled: disableShareButton,
        name: loc(bookwithme_shareBookingButtonLabel),
        onClick: isFRETriggered ? dismissClick : onEmailLinkButtonClick,
    };

    const makePrivateMeetingTypeMenuItem: IContextualMenuItem = {
        key: 'MakePrivateMeetingType',
        text: loc(bookWithMeMakePrivateText),
        title: loc(bookWithMeMakePrivateTooltip),
        iconProps: {
            iconName: LockClosedRegular,
            styles: {
                root: isUnifiedLandingPage ? contextualMenuItemUnified : contextualMenuItem,
            },
        },
        onClick: isFRETriggered ? dismissClick : onMakePrivateMeetingTypeButtonClick,
    };

    const makePublicMeetingTypeMenuItem: IContextualMenuItem = {
        key: 'makePublicMeetingType',
        text: loc(bookWithMeMakePublicText),
        title: loc(bookWithMeMakePublicTooltip),
        iconProps: {
            iconName: LockOpenRegular,
            styles: {
                root: isUnifiedLandingPage ? contextualMenuItemUnified : contextualMenuItem,
            },
        },
        onClick: onMakePublicMeetingTypeButtonClick,
    };

    const deleteMeetingTypeMenuItem: IContextualMenuItem = {
        key: 'DeleteMeetingType',
        text: loc(bookWithMeDeleteButtonLabel),
        title: loc(bookWithMeDeleteButtonTooltip),
        iconProps: {
            iconName: DeleteRegular,
            styles: {
                root: isUnifiedLandingPage ? contextualMenuItemUnified : contextualMenuItem,
            },
        },
        onClick: isFRETriggered ? dismissClick : onDeleteMeetingTypeButtonClicked,
    };

    const duplicateMeetingTypeMenuItem: IContextualMenuItem = {
        key: 'DuplicateMeetingType',
        text: loc(bookwithme_duplicateMeetingTypeButtonLabel),
        title: loc(bookwithme_duplicateMeetingTypeButtonTooltip),
        iconProps: {
            iconName: SaveCopyRegular,
            styles: {
                root: isUnifiedLandingPage ? contextualMenuItemUnified : contextualMenuItem,
            },
        },
        onClick: isFRETriggered ? dismissClick : onDuplicateMeetingTypeButtonClicked,
    };

    const stopSharingMeetingTypeMenuItem: IContextualMenuItem = {
        key: 'StopSharingMeetingType',
        text: loc(bookwithme_stopSharingMeetingTypeButtonLabel),
        title: loc(bookwithme_stopSharingMeetingTypeButtonTooltip),
        iconProps: {
            iconName: LinkDismissRegular,
            styles: {
                root: isUnifiedLandingPage ? contextualMenuItemUnified : contextualMenuItem,
            },
        },
        onClick: onStopSharingMeetingTypeButtonClick,
    };

    const publicMoreOptions: IContextualMenuItem[] = [
        copyLinkMenuItem,
        shareViaEmailMenuItem,
        duplicateMeetingTypeMenuItem,
        makePrivateMeetingTypeMenuItem,
        deleteMeetingTypeMenuItem,
    ];

    const privateMoreOptions: IContextualMenuItem[] = [
        copyLinkMenuItem,
        copySingleUseLinkMenuItem,
        shareViaEmailMenuItem,
        duplicateMeetingTypeMenuItem,
        makePublicMeetingTypeMenuItem,
        deleteMeetingTypeMenuItem,
    ];

    const publicMoreOptionsUnified: IContextualMenuItem[] = [
        copyLinkMenuItem,
        shareViaEmailMenuItem,
        duplicateMeetingTypeMenuItem,
        makePrivateMeetingTypeMenuItem,
        deleteMeetingTypeMenuItem,
    ];

    const privateMoreOptionsUnified: IContextualMenuItem[] = [
        copyLinkMenuItem,
        copySingleUseLinkMenuItem,
        duplicateMeetingTypeMenuItem,
        makePublicMeetingTypeMenuItem,
        deleteMeetingTypeMenuItem,
    ];

    const unpublishedPublicMoreOptions: IContextualMenuItem[] = [
        duplicateMeetingTypeMenuItem,
        makePrivateMeetingTypeMenuItem,
        deleteMeetingTypeMenuItem,
    ];

    const unpublishedPrivateMoreOptions: IContextualMenuItem[] = [
        duplicateMeetingTypeMenuItem,
        makePublicMeetingTypeMenuItem,
        deleteMeetingTypeMenuItem,
    ];

    let itemMoreOptions: IContextualMenuItem[] = [];
    if (getPublishedStatus()) {
        if (isUnifiedLandingPage) {
            itemMoreOptions = isPrivateMeetingType
                ? privateMoreOptionsUnified
                : publicMoreOptionsUnified;
        } else {
            itemMoreOptions = isPrivateMeetingType ? privateMoreOptions : publicMoreOptions;
        }
        if (!meetingType.ExcludeFromSelfService && isFeatureEnabled('openbook-showStopSharing')) {
            itemMoreOptions.push(stopSharingMeetingTypeMenuItem);
        }
    } else {
        itemMoreOptions = isPrivateMeetingType
            ? unpublishedPrivateMoreOptions
            : unpublishedPublicMoreOptions;
    }

    const moreOptionsMenuProps = {
        items: itemMoreOptions,
    };

    return {
        ...moreOptionsMenuProps,
        styles: { list: { pointerEvents: isFRETriggered ? 'none' : undefined } },
    };
}
